import { Injectable } from '@angular/core';
import { UrlMatchResult, UrlSegment } from '@angular/router';

// r8ui's /user-management/<page> frames v11's /v11/admin/<page> on hosts that
// serve both apps, so each v11 admin page has an r8ui URL of its own.
const PAGE = '/user-management';
const ADMIN = '/v11/admin';
const FIRST = 'users';

/** The v11 admin URL to frame for an r8ui /user-management URL. */
export function toFrame(url: string): string {
  const rest = url.slice(PAGE.length).replace(/^\//, '');
  return `${ADMIN}/${rest && rest[0] !== '?' ? rest : FIRST + rest}`;
}

/** The r8ui URL for a framed v11 URL, or null outside v11's admin pages. */
export function toPage(frameUrl: string): string | null {
  const page = new RegExp(`^${ADMIN}/([^/?].*)$`).exec(frameUrl);
  return page ? `${PAGE}/${page[1]}` : null;
}

export interface V11Page { page: string; label: string; groups: string[]; }

// The admin pages r8ui frames, in tab order, and the groups v11 lets open each:
// rv11's ui/src/assets/admin/permissions.json (pagePermissions), which v11 compiles
// into its bundle, so it can't be read from /v11 at runtime. v11 enforces these
// itself; r8ui only keeps from offering a page v11 would turn the user away from.
export const PAGES: V11Page[] = [
  { page: 'users', label: 'Users', groups: ['custodian', 'custodian-support', 'admin'] },
  { page: 'groups', label: 'Groups', groups: ['custodian', 'custodian-support', 'admin'] },
  { page: 'sso', label: 'SSO', groups: ['custodian'] },
  { page: 'sessions', label: 'Sessions', groups: ['custodian'] }
];

/** Every group that can open some framed page: who the /user-management route lets in. */
export const ADMIN_GROUPS = ['custodian', 'custodian-support', 'admin'];

/** The framed pages a user in these groups may open (getUserGroups() is undefined for none). */
export function allowedPages(groups: string[] | undefined): V11Page[] {
  return PAGES.filter(p => (groups || []).some(g => p.groups.indexOf(g) != -1));
}

/**
 * An r8ui /user-management URL as the user may open it: unchanged when they may
 * open its page (bare /user-management being Users), else their first page, or
 * null when they may open none.
 */
export function allowedUrl(url: string, groups: string[] | undefined): string | null {
  const pages = allowedPages(groups);
  const page = url.slice(PAGE.length).replace(/^\//, '').split(/[/?]/)[0] || FIRST;
  if (pages.some(p => p.page === page)) {
    return url;
  }
  return pages.length ? `${PAGE}/${pages[0].page}` : null;
}

/** /user-management and everything below it, as one route so the frame survives page changes. */
export function userManagementMatcher(segments: UrlSegment[]): UrlMatchResult {
  return segments.length && segments[0].path === PAGE.slice(1) ? { consumed: segments } : null;
}

/**
 * Whether this host serves v11 at /v11, and whether the browser holds the ALB
 * session it sits behind: /v11/ answers 404 where it isn't served, redirects to
 * the login without a session and 200s with one.
 */
export type V11Status = 'absent' | 'signed-out' | 'signed-in';

@Injectable({ providedIn: 'root' })
export class V11Service {
  private probe: Promise<boolean>;

  status(): Promise<V11Status> {
    return fetch('/v11/', { redirect: 'manual' }).then(res =>
      res.type === 'opaqueredirect' ? 'signed-out' : res.ok ? 'signed-in' : 'absent');
  }

  /** Asked once per page load: the header shows User Management only where v11 is served. */
  served(): Promise<boolean> {
    return this.probe = this.probe || this.status().then(s => s !== 'absent', () => false);
  }
}
